export type NavLink = {
  label: string
  href: string
}

export type NavItem = NavLink & {
  children?: NavLink[]
}

export const business = {
  name: 'Pegasus Spas & Leisure Products',
  shortName: 'Pegasus Spas',
  url: 'https://www.pegasus-spas.com/',
  street: '171 South Main St',
  cityStateZip: 'Brooklyn, MI 49230',
  city: 'Brooklyn',
  state: 'MI',
  zip: '49230',
  phone: '517-592-3227',
  phoneHref: 'tel:+15175923227',
  smsHref: 'sms:+15175923227',
  hours: [
    { days: 'Monday–Friday', time: '10:00am–6:00pm' },
    { days: 'Saturday', time: '9:00am–3:00pm' },
  ],
  localBar:
    'Hot Tubs, Outdoor Furniture & Spa Service | Serving Brooklyn, Michigan and surrounding areas since 1977',
  footerSeo:
    'We are a family-owned hot tub, outdoor furniture and spa service showroom in Brooklyn, Michigan, serving Jackson, Lenawee, Hillsdale and Washtenaw counties and surrounding areas since 1977.',
}

export const routes = {
  home: '/',
  hotTubs: '/hot-tubs',
  sundance: '/hot-tubs/sundance-spas',
  viking: '/hot-tubs/viking-spas',
  availableSpas: '/hot-tubs/available-spas',
  preOwned: '/hot-tubs/certified-pre-owned',
  financing: '/financing',
  furniture: '/backyard-living',
  grills: '/backyard-living/big-green-egg',
  fireTables: '/backyard-living/fire-tables',
  umbrellas: '/backyard-living/umbrellas',
  spaSteps: '/backyard-living/steps',
  service: '/service',
  spaCare: '/service/spa-care',
  serviceGuarantee: '/service/work-guarantee',
  supplies: '/shop',
  filters: '/shop/filters',
  testing: '/shop/testing',
  maintenance: '/shop/maintenance',
  spaChemicals: '/shop/spa-chemicals',
  poolChemicals: '/shop/pool-chemicals',
  bigGreenEggAccessories: '/shop/big-green-egg-accessories',
  naturalChemicals: '/shop/natural-chemicals',
  productDetail: '/shop/product/viking-spas-replacement-filter',
  covers: '/covers',
  coverLifts: '/covers/cover-lifts',
  about: '/about',
  reviews: '/about/google-reviews',
  location: '/location',
  referrals: '/referrals',
  specials: '/specials',
  contact: '/contact',
  coldPlunge: '/cold-plunge-tubs',
  health: '/health-benefits',
  blog: '/blog',
  facebook: 'https://www.facebook.com/PegasusSpas/',
}

/** Every non-home destination in the site, rendered as a placeholder page for now. */
export const placeholderPages: { path: string; title: string }[] = [
]

/**
 * Former online shop categories that are no longer part of shop browsing.
 * Old links still resolve to a compact "ask us in the showroom" page.
 */
export const retiredShopPaths: { path: string; label: string }[] = [
  { path: '/shop/brumate', label: 'BruMate drinkware' },
  { path: '/shop/music-speakers', label: 'music and speakers' },
  { path: '/shop/spinners-windsocks', label: 'spinners and windsocks' },
  { path: '/shop/winter-fun-accessories', label: 'winter accessories' },
]

/**
 * Special-order lead-time message shown on outdoor-furniture pages.
 * Edit the text here; every page that shows the notice reads from this object.
 */
export const leadTimeNotice = {
  title: 'Special Order Lead Times',
  paragraphs: [
    'Current lead times on some outdoor furniture collections have increased to approximately 25 business days, plus shipping.',
    'Availability and delivery times vary by manufacturer and product.',
  ],
}

export const navigation: NavItem[] = [
  {
    label: 'Hot Tubs',
    href: routes.hotTubs,
    children: [
      { label: 'Explore Hot Tubs', href: routes.hotTubs },
      { label: 'Sundance Spas', href: routes.sundance },
      { label: 'Viking Spas', href: routes.viking },
      { label: 'Available Spas', href: routes.availableSpas },
      { label: 'Certified Pre-Owned Spas', href: routes.preOwned },
      { label: 'Financing', href: routes.financing },
    ],
  },
  {
    label: 'Backyard Living',
    href: routes.furniture,
    children: [
      { label: 'Outdoor Furniture', href: routes.furniture },
      { label: 'Big Green Egg Grills', href: routes.grills },
      { label: 'Fire Tables', href: routes.fireTables },
      { label: 'Umbrellas', href: routes.umbrellas },
      { label: 'Hot Tub Steps', href: routes.spaSteps },
      { label: 'Cold Plunge Tubs', href: routes.coldPlunge },
    ],
  },
  {
    label: 'Service',
    href: routes.service,
    children: [
      { label: 'Service & Repair', href: routes.service },
      { label: 'Spa Care & FAQs', href: routes.spaCare },
      { label: 'Service Work Guarantee', href: routes.serviceGuarantee },
    ],
  },
  {
    label: 'Covers',
    href: routes.covers,
    children: [
      { label: 'Premium Replacement Covers', href: routes.covers },
      { label: 'Cover Lifts', href: routes.coverLifts },
    ],
  },
  {
    label: 'Shop Online',
    href: routes.supplies,
    children: [
      { label: 'Supplies & Chemicals', href: routes.supplies },
      { label: 'Filters', href: routes.filters },
      { label: 'Spa Chemicals', href: routes.spaChemicals },
      { label: 'Pool Chemicals', href: routes.poolChemicals },
      { label: 'Testing & Care Supplies', href: routes.maintenance },
      { label: 'Big Green Egg Accessories', href: routes.bigGreenEggAccessories },
    ],
  },
  {
    label: 'About',
    href: routes.about,
    children: [
      { label: 'Why Pegasus', href: routes.about },
      { label: 'Our Story', href: routes.about },
      { label: 'Google Reviews', href: routes.reviews },
      { label: 'Blog', href: routes.blog },
      { label: 'Contact, Location & Hours', href: routes.location },
    ],
  },
  {
    label: 'Specials',
    href: routes.specials,
    children: [
      { label: 'Current Specials', href: routes.specials },
      { label: 'Soak & Share', href: routes.referrals },
      { label: 'Available Spas', href: routes.availableSpas },
    ],
  },
]

export const images = {
  logo: 'https://cdn.magicpatterns.com/uploads/emcb92Q1bKmEtQ6yTzYfmP/pegasus-logo-current.jpg',
  hero: 'https://cdn.magicpatterns.com/uploads/v2J8M4fQ2uPUg4S31nXXCf/pegasus-hero-natural-2752.jpg',
  pathHotTubs: 'https://cdn.magicpatterns.com/patterns/generated-images/3e3c9eca-c3e1-4756-8bfc-0fd41cc6c7b0.jpg',
  pathBackyard: 'https://cdn.magicpatterns.com/uploads/4R7XoFCfKYQSFmSfCAnEgR/backyard-living-category.png',
  pathColdPlunge: 'https://cdn.magicpatterns.com/uploads/7PByUM3JAyeEg4ZTX4L3i5/cold-plunge-garage-4.png',
  pathService: 'https://cdn.magicpatterns.com/uploads/uS2xQnZHvjhzYkhkBszDiU/service-category.png',
  pathCovers: 'https://cdn.magicpatterns.com/uploads/cLm6ht6qgismk77hQEJGhX/cover-category.png',
  whyPegasus: 'https://cdn.magicpatterns.com/uploads/cwfdFU7oWDBViHd6YXzbfB/Store-Location.jpg',
  sundance: 'https://cdn.magicpatterns.com/uploads/tD9Dhi54dv7YF3Vf43kGse/sundance-official.jpg',
  viking: 'https://cdn.magicpatterns.com/uploads/8YHcpivwCrfdNzved8WvN1/viking-regal-model-accurate-card-4x3.png',
  backyardLiving: 'https://cdn.magicpatterns.com/uploads/kAmHaLKik56LRCYt3Yd8xn/outdoor-living-deck-integrated-1800.jpg',
  /** Real photos from one of our lake-access deliveries, supplied by the owner. */
  deliveryDock: 'https://cdn.magicpatterns.com/uploads/wV5tQrTJqq2EeWxAroqeLz/IMG_5014.jpg',
  deliveryBarge: 'https://cdn.magicpatterns.com/uploads/iep6oKCSQfZmSN9yfn2U43/IMG_5013.jpg',
  /**
   * Temporary AI team-layout concept approved by Kevin for layout review only.
   * The "Updated photo needed" banner is part of the image and must stay visible.
   */
  teamConcept:
    'https://cdn.magicpatterns.com/uploads/dxZRJsSBgbqe2VoBCDdktA/pegasus-team-layout-concept-updated-photo-needed.png',
  /** Official Sundance Spas lifestyle and water-care photography. */
  sundanceCouple:
    'https://cdn.prod.website-files.com/698a3062bdbead7a29341917/69d3f6eec651fcac4c111ab5_SDS_780_Chelsee_VintageOak_Moment_Couple_12jpg-2100x1393-65e34794-0c48-4d0c-be10-a7488019c6d8.avif',
  sundanceFilter:
    'https://cdn.prod.website-files.com/698a3062bdbead7a29341917/69d6b3e4827b8e9f0cdaa688_SDS_880_Cameo_Platinum_Filter_Mom_07-2100x1400-f3b42d26-78c4-49c2-bbec-76d4214174efcopy.avif',
  /** Used only by the /specials hero. */
  specialsHero:
    'https://cdn.magicpatterns.com/uploads/5TpZzuEybtJaDARqBpaNJ4/pegasus-specials-candidate-2.png',
}

export const deliveryDockAlt =
  'Two Pegasus team members guiding a wrapped hot tub loaded onto a barge at a lakeside dock on an overcast day'
export const deliveryBargeAlt =
  'A barge carrying a hot tub across open lake water toward the far shore'

/**
 * Per-route interior hero photography.
 *
 * Every entry owns its own image, alt text and responsive focal point. These
 * are deliberately NOT shared between routes: each panorama places its subject
 * differently, so a single object-position cannot keep every product in frame.
 *
 * The panoramas are 21:9, so at wide desktop widths the frame is wider than the
 * image and only the vertical axis is cropped — the vertical focal value is
 * what keeps the named product inside the band. At tablet and mobile the full
 * image height is visible and the horizontal value does the work instead.
 */
type InteriorHero = {
  src: string
  alt: string
  width: number
  height: number
  frameClass: string
  contentClass: string
  imageClass: string
}

const panoramaSize = { width: 2048, height: 878 }

const heroFrame = (heights: string) => ({
  frameClass: `min-h-[400px] sm:min-h-[380px] ${heights}`,
  contentClass: `min-h-[400px] sm:min-h-[380px] ${heights} lg:min-h-0`,
})

export const interiorHeroes: Record<
  | 'coldPlunge'
  | 'backyardLiving'
  | 'bigGreenEgg'
  | 'fireTables'
  | 'umbrellas'
  | 'spaSteps',
  InteriorHero
> = {
  coldPlunge: {
    src: 'https://cdn.magicpatterns.com/uploads/8oBCR4Xeq4HbkawGvTFCFf/pegasus-cold-plunge-4.png',
    alt: 'Complete Viking Spas Chill Therapy cold plunge with matching step in a clean Michigan garage workout area.',
    ...panoramaSize,
    ...heroFrame('lg:h-[440px]'),
    imageClass:
      'absolute inset-0 h-full w-full object-cover [object-position:75%_50%] sm:[object-position:60%_50%] lg:[object-position:60%_78%]',
  },
  backyardLiving: {
    src: 'https://cdn.magicpatterns.com/uploads/s4bx4vba7hLUsWchMfgURV/pegasus-furniture-3.png',
    alt: 'All-weather outdoor sofa, lounge chairs and dining furniture on a modest Michigan patio.',
    ...panoramaSize,
    ...heroFrame('lg:h-[460px]'),
    imageClass:
      'absolute inset-0 h-full w-full object-cover [object-position:65%_50%] sm:[object-position:60%_50%] lg:[object-position:60%_80%]',
  },
  bigGreenEgg: {
    src: 'https://cdn.magicpatterns.com/uploads/79LDJkexkAwFr5RqjwsD6s/pegasus-big-green-egg-2.png',
    alt: 'Full green ceramic kamado cooker in use on a Michigan backyard patio.',
    ...panoramaSize,
    ...heroFrame('lg:h-[500px] 2xl:h-[560px]'),
    imageClass:
      'absolute inset-0 h-full w-full object-cover [object-position:65%_50%] sm:[object-position:62%_50%] lg:[object-position:62%_55%]',
  },
  fireTables: {
    src: 'https://cdn.magicpatterns.com/uploads/gYQcqwg6zT62ST3Ma2Yehw/pegasus-fire-table-2.png',
    alt: 'Rectangular gas fire table with visible flames and outdoor lounge seating on a Michigan patio.',
    ...panoramaSize,
    ...heroFrame('lg:h-[460px]'),
    imageClass:
      'absolute inset-0 h-full w-full object-cover [object-position:75%_50%] sm:[object-position:60%_50%] lg:[object-position:60%_70%]',
  },
  umbrellas: {
    src: 'https://cdn.magicpatterns.com/uploads/whw9h82bMYf9zfVDu6JEZX/pegasus-umbrella-2.png',
    alt: 'Fully opened market umbrella shading a six-seat dining table on a Michigan patio.',
    ...panoramaSize,
    ...heroFrame('lg:h-[480px] 2xl:h-[540px]'),
    imageClass:
      'absolute inset-0 h-full w-full object-cover [object-position:72%_50%] sm:[object-position:60%_50%] lg:[object-position:60%_42%]',
  },
  spaSteps: {
    src: 'https://cdn.magicpatterns.com/uploads/55qXCLAHT6Q2VXttNJAtEC/pegasus-hot-tub-steps-2.png',
    alt: 'Two-tier slip-resistant hot tub steps with a handrail beside a residential spa.',
    ...panoramaSize,
    ...heroFrame('lg:h-[460px]'),
    imageClass:
      'absolute inset-0 h-full w-full object-cover [object-position:68%_50%] sm:[object-position:58%_50%] lg:[object-position:58%_75%]',
  },
}

export const trustPoints = [
  'Serving local families since 1977',
  'A real showroom in Brooklyn, MI',
  'Service and support after the sale',
  'Michigan-ready products',
]

export type Pathway = {
  title: string
  description: string
  href: string
  image: string
  alt: string
}

export const pathways: Pathway[] = [
  {
    title: 'Hot Tubs',
    description: 'Explore Sundance, Viking, available and certified pre-owned spas.',
    href: routes.hotTubs,
    image: images.pathHotTubs,
    alt: 'Hot tub with clear water and jets running on a residential wood deck surrounded by trees',
  },
  {
    title: 'Backyard Living',
    description: 'Outdoor furniture, Big Green Egg Grills, fire tables, umbrellas and more.',
    href: routes.furniture,
    image: images.pathBackyard,
    alt: 'Outdoor furniture, fire table, umbrella, green ceramic grill and hot tub on a Michigan lake patio.',
  },
  {
    title: 'Cold Plunge Tubs',
    description: 'Explore cold-plunge options for home.',
    href: routes.coldPlunge,
    image: images.pathColdPlunge,
    alt: 'Viking Spas Chill Therapy cold plunge in a clean climate-controlled Michigan garage workout area.',
  },
  {
    title: 'Spa Service & Care',
    description: 'Get help with service, repairs, water care and common questions.',
    href: routes.service,
    image: images.pathService,
    alt: 'Spa technician inspecting plumbing inside an open hot tub equipment bay.',
  },
  {
    title: 'Replacement Covers',
    description: 'Build a custom replacement cover for your spa, step by step.',
    href: routes.covers,
    image: images.pathCovers,
    alt: 'New fitted graphite replacement spa cover with taut vinyl and a clean center hinge.',
  },
]

export const utilityLinks: NavLink[] = [
  { label: 'Shop Spa & Pool Supplies', href: routes.supplies },
  { label: 'View Current Specials', href: routes.specials },
  { label: 'Financing Options', href: routes.financing },
]

/**
 * Owner-editable external destination for the full Google review listing.
 * Replace with the exact Google Business Profile review URL when it is supplied.
 */
export const googleReviewsExternalUrl =
  'https://www.google.com/maps/search/?api=1&query=Pegasus+Spas+171+South+Main+St+Brooklyn+MI+49230'

/** Verified five-star Google review excerpts used in the compact homepage teaser. */
export const googleReviewTeasers: { name: string; excerpt: string }[] = [
  {
    name: 'Denise Shelle',
    excerpt: 'The team that delivered the Spa and furniture were TOP NOTCH!!!',
  },
  {
    name: 'Melinda “Molly” Serafin',
    excerpt: 'Pegasus Spas & Leisure takes care of their customers!!!!!!',
  },
]

export const whyPoints = [
  'Family-owned and locally established',
  'Long-standing manufacturer relationships',
  'Showroom guidance before you buy',
  'Spa care and service support afterward',
]

export const brandRoutes = [
  {
    name: 'Sundance Spas',
    description:
      'Premium hydrotherapy, thoughtful comfort and advanced water care for everyday relaxation.',
    href: routes.sundance,
    image: images.sundance,
    modelLabel: 'Model: Cameo',
    alt: 'Sundance Spas Cameo hot tub in a landscaped backyard',
  },
  {
    name: 'Viking Spas',
    description:
      'American-built durability, practical comfort and flexible models, including plug-and-play options.',
    href: routes.viking,
    image: images.viking,
    modelLabel: 'Model: Regal',
    alt: 'Viking Spas Regal hot tub installed beside a modest Michigan home and deck',
  },
]

export const hotTubLinks: NavLink[] = [
  { label: 'See Available Spas', href: routes.availableSpas },
  { label: 'Certified Pre-Owned Spas', href: routes.preOwned },
  { label: 'Financing Options', href: routes.financing },
]

export const backyardLinks: NavLink[] = [
  { label: 'Explore Outdoor Furniture', href: routes.furniture },
  { label: 'Big Green Egg Grills', href: routes.grills },
  { label: 'Fire Tables', href: routes.fireTables },
  { label: 'Umbrellas', href: routes.umbrellas },
]

export const serviceLinks: NavLink[] = [
  { label: 'Get Spa Service Help', href: routes.service },
  { label: 'Explore Spa Care', href: routes.spaCare },
  { label: 'Service Work Guarantee', href: routes.serviceGuarantee },
  { label: 'Shop Supplies & Filters', href: routes.supplies },
]

export const specialsCopy = {
  eyebrow: 'Current Specials',
  heading: "See What's Happening at Pegasus Right Now",
  body:
    'Offers change throughout the year. See what we have going on now, or call or text us for today’s offers.',
}

export const footerGroups: { title: string; links: NavLink[] }[] = [
  {
    title: 'Hot Tubs',
    links: [
      { label: 'Explore Hot Tubs', href: routes.hotTubs },
      { label: 'Sundance Spas', href: routes.sundance },
      { label: 'Viking Spas', href: routes.viking },
      { label: 'Available Spas', href: routes.availableSpas },
      { label: 'Certified Pre-Owned Spas', href: routes.preOwned },
      { label: 'Financing', href: routes.financing },
    ],
  },
  {
    title: 'Backyard Living',
    links: [
      { label: 'Outdoor Furniture', href: routes.furniture },
      { label: 'Big Green Egg Grills', href: routes.grills },
      { label: 'Fire Tables', href: routes.fireTables },
      { label: 'Umbrellas', href: routes.umbrellas },
      { label: 'Hot Tub Steps', href: routes.spaSteps },
      { label: 'Cold Plunge Tubs', href: routes.coldPlunge },
    ],
  },
  {
    title: 'Service & Spa Care',
    links: [
      { label: 'Service & Repair', href: routes.service },
      { label: 'Spa Care & FAQs', href: routes.spaCare },
      { label: 'Service Work Guarantee', href: routes.serviceGuarantee },
      { label: 'Premium Replacement Covers', href: routes.covers },
      { label: 'Cover Lifts', href: routes.coverLifts },
    ],
  },
  {
    title: 'Shop Online',
    links: [
      { label: 'Supplies & Chemicals', href: routes.supplies },
      { label: 'Filters', href: routes.filters },
      { label: 'Spa Chemicals', href: routes.spaChemicals },
      { label: 'Pool Chemicals', href: routes.poolChemicals },
      { label: 'Testing & Care Supplies', href: routes.maintenance },
      { label: 'Big Green Egg Accessories', href: routes.bigGreenEggAccessories },
    ],
  },
  {
    title: 'About',
    links: [
      { label: 'Why Pegasus', href: routes.about },
      { label: 'Our Story', href: routes.about },
      { label: 'Google Reviews', href: routes.reviews },
      { label: 'Blog', href: routes.blog },
      { label: 'Health Benefits', href: routes.health },
    ],
  },
  {
    title: 'Visit & Connect',
    links: [
      { label: 'Current Specials', href: routes.specials },
      { label: 'Soak & Share', href: routes.referrals },
      { label: 'Location & Hours', href: routes.location },
      { label: 'Contact', href: routes.contact },
      { label: 'Facebook', href: routes.facebook },
    ],
  },
]

